// import { Box, Button, FormControl, Typography } from '@mui/material';
// import { useField, useFormikContext } from 'formik';
// import React from 'react';
// import { resizeImage } from './resizeImage';
// import { uploadImage } from '../../HelperFunctions/s3Service';

// interface ImageUploadFieldProps {
//     name: string;
//     label: string;
//     multiple?: boolean;
//     maxFiles?: number;
//     recommendedText?: string;
// }

// const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
//     name,
//     label,
//     multiple = false,
//     maxFiles = 1,
//     recommendedText,
// }) => {
//     const [field, meta] = useField(name);
//     const form = useFormikContext<any>();

//     const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
//         const files = Array.from(e.target.files || []);
//         form.setFieldTouched(name, true);

//         if (files.length === 0) {
//             form.setFieldError(name, `Please upload ${label.toLowerCase()}`);
//             return;
//         }

//         const existing = multiple ? field.value || [] : [];
//         if (multiple && existing.length + files.length > maxFiles) {
//             form.setFieldError(name, `Maximum ${maxFiles} ${label.toLowerCase()} images allowed`);
//             return;
//         }

//         try {
//             const resizedFiles = await Promise.all(files.map(file => resizeImage(file, label)));
//             const uploaded = await Promise.all(resizedFiles.map(file => uploadImage(file)));
//             const urls = uploaded.map(res => res.url);

//             const newValue = multiple ? [...existing, ...urls] : urls[0];
//             form.setFieldValue(name, newValue);
//             form.setFieldError(name, undefined);
//         } catch (err) {
//             console.error(err);
//             form.setFieldError(
//                 name,
//                 err instanceof Error ? err.message : `Error uploading ${label.toLowerCase()}`
//             );

//         }
//     };

//     const handleClear = () => {
//         form.setFieldValue(name, multiple ? [] : null);
//         form.setFieldError(name, undefined);
//     };

//     const fileInputId = `${name}-input`;
//     const hasValue = multiple ? Array.isArray(field.value) && field.value.length > 0 : !!field.value;

//     return (
//         <FormControl error={Boolean(meta.touched && meta.error)} fullWidth>
//             <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
//                 <Box sx={{ flex: 1 }}>
//                     <input
//                         type="file"
//                         accept="image/*"
//                         multiple={multiple}
//                         onChange={handleChange}
//                         style={{ display: 'none' }}
//                         id={fileInputId}
//                     />

//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                         <label htmlFor={fileInputId}>
//                             <Button
//                                 variant="outlined"
//                                 component="span"
//                                 sx={{
//                                     textTransform: 'uppercase',
//                                     height: '35px',
//                                     bgcolor: 'background.paper',
//                                     color: 'black',
//                                     borderColor: 'black',
//                                     '&:hover': {
//                                         borderColor: 'black',
//                                         backgroundColor: 'rgba(0, 0, 0, 0.04)',
//                                     },
//                                 }}
//                             >
//                                 Choose File
//                             </Button>
//                         </label>

//                         <Typography variant="body2" color="text.secondary">
//                             {hasValue
//                                 ? multiple
//                                     ? `${field.value.length} image${field.value.length > 1 ? 's' : ''} uploaded`
//                                     : 'File selected'
//                                 : 'No file chosen'}
//                         </Typography>

//                         {hasValue && (
//                             <Button
//                                 variant="contained"
//                                 color="error"
//                                 onClick={handleClear}
//                                 sx={{ height: '30px', textTransform: 'uppercase' }}
//                             >
//                                 Clear
//                             </Button>
//                         )}
//                     </Box>

//                     {/* Preview Section */}
//                     {hasValue && (
//                         <Box mt={2} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
//                             {(multiple ? field.value : [field.value]).map((url: string, idx: number) => (
//                                 <Box key={idx} sx={{ position: 'relative' }}>
//                                     <img
//                                         src={url}
//                                         alt={`${label} ${idx + 1}`}
//                                         style={{
//                                             width: multiple ? 200 : 150,
//                                             height: multiple ? 100 : 150,
//                                             objectFit: 'cover',
//                                             borderRadius: 4,
//                                             border: '1px solid #ddd',
//                                         }}
//                                     />
//                                 </Box>
//                             ))}
//                         </Box>
//                     )}

//                     {/* Text under image preview */}
//                     {recommendedText && (
//                         <Typography
//                             variant="caption"
//                             color="text.secondary"
//                             sx={{ display: 'block', mt: 1 }}
//                         >
//                             {recommendedText}
//                         </Typography>
//                     )}
//                 </Box>
//             </Box>
//         </FormControl>
//     );
// };

// export default ImageUploadField;


import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { useField, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resizeImage } from './resizeImage';
import { uploadImageWithPresignedUrl } from '../../HelperFunction/s3Service';
import { useContext } from 'react';
import { HimmaContext } from '../../../Context/HimmaContext';


interface ImageUploadFieldProps {
    name: string;
    label: string;
    multiple?: boolean;
    maxFiles?: number;
    recommendedText?: string;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
    name,
    label,
    multiple = false,
    maxFiles = 1,
    recommendedText,
}) => {
    const [field, meta] = useField(name);
    const form = useFormikContext<any>();
    const { t } = useTranslation();
    const apiClient = useContext(HimmaContext);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        form.setFieldTouched(name, true);

        if (files.length === 0) {
            form.setFieldError(name, t('common.pleaseUpload', { label: label.toLowerCase() }));
            return;
        }

        const existing = multiple ? field.value || [] : [];
        if (multiple && existing.length + files.length > maxFiles) {
            form.setFieldError(name, t('common.maximumImagesAllowedWithLabel', { count: maxFiles, label: label.toLowerCase() }));
            return;
        }

        try {
            if (!apiClient) {
                throw new Error('API client not available');
            }

            const resizedFiles = await Promise.all(files.map(file => resizeImage(file, label)));
            const uploaded = await Promise.all(resizedFiles.map(file => uploadImageWithPresignedUrl(file, apiClient)));
            const urls = uploaded.map(res => res.url);

            const newValue = multiple ? [...existing, ...urls] : urls[0];
            form.setFieldValue(name, newValue);
            form.setFieldError(name, undefined);
        } catch (err) {
            console.error(err);

            const errorMessage =
                typeof err === 'string'
                    ? err
                    : err instanceof Error
                        ? err.message
                        : t('common.errorUploading', { label: label.toLowerCase() });

            form.setFieldError(name, errorMessage);
        }

    };

    const handleClear = () => {
        form.setFieldValue(name, multiple ? [] : null);
        form.setFieldError(name, undefined);
        form.setFieldTouched(name, true, false);
    };

    const fileInputId = `${name}-input`;
    const hasValue = multiple ? Array.isArray(field.value) && field.value.length > 0 : !!field.value;
    const uploadedImages = React.useMemo(() => {
        if (!field.value) return [];

        if (multiple) {
            // Normalize: extract url from objects if needed
            return (field.value as any[]).map(img =>
                typeof img === 'string' ? img : img?.url || ''
            ).filter(Boolean);
        }

        // Single image case
        const single = field.value;
        if (typeof single === 'string') return [single];
        if (typeof single === 'object' && single?.url) return [single.url];
        return [];
    }, [field.value, multiple]);
    return (
        <FormControl error={Boolean(meta.touched && meta.error)} fullWidth>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <input
                        type="file"
                        accept="image/*"
                        multiple={multiple}
                        onChange={handleChange}
                        style={{ display: 'none' }}
                        id={fileInputId}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <label htmlFor={fileInputId}>
                            <Button
                                variant="outlined"
                                component="span"
                                sx={{
                                    textTransform: 'uppercase',
                                    height: '35px',
                                    bgcolor: 'background.paper',
                                    color: 'black',
                                    borderColor: 'black',
                                    '&:hover': {
                                        borderColor: 'black',
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                {t('storeItem.form.chooseFile')}
                            </Button>
                        </label>

                        <Typography variant="body2" color="text.secondary">
                            {hasValue
                                ? multiple
                                    ? t('storeItem.form.imagesSelected', { count: field.value.length })
                                    : t('storeItem.form.fileSelected')
                                : t('storeItem.form.noFileChosen')}
                        </Typography>

                        {hasValue && (
                            <>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleClear}
                                    sx={{ height: '30px', textTransform: 'uppercase' }}
                                    >
                                        {t('common.clear')}
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={() => setPreviewOpen(true)}
                                    sx={{ height: '30px', textTransform: 'uppercase' }}
                                >
                                        {t('common.preview')}
                                </Button>
                            </>
                        )}
                    </Box>

                    {/* Image Thumbnails */}
                    {/* {hasValue && (
            <Box mt={2} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {uploadedImages.map((url: string, idx: number) => (
                <Box key={idx} sx={{ position: 'relative' }}>
                  <img
                    src={url}
                    alt={`${label} ${idx + 1}`}
                    style={{
                      width: multiple ? 200 : 150,
                      height: multiple ? 100 : 150,
                      objectFit: 'cover',
                      borderRadius: 4,
                      border: '1px solid #ddd',
                    }}
                  />
                </Box>
              ))}
            </Box>
          )} */}

                    {/* Recommended Text */}
                    {recommendedText && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}
                        >
                            {recommendedText}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{t('common.preview')} {label}</DialogTitle>
                <DialogContent>
                    <Tabs value={tabIndex} onChange={(_, newVal) => setTabIndex(newVal)} sx={{ mb: 2 }}>
                        <Tab label={t('storeItem.form.mobileImage')} />
                        <Tab label={t('storeItem.form.webImage')} />
                    </Tabs>

                    {uploadedImages.map((url: string, idx: number) => (
                        <Box key={idx} sx={{ display: tabIndex === 0 ? 'block' : 'none' }}>
                            {/* Mobile Preview */}
                            <Box sx={{ maxWidth: 400, mx: 'auto', }}>
                                <img
                                    src={url}
                                    alt={`Mobile Preview ${idx + 1}`}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}

                    {uploadedImages.map((url: string, idx: number) => (
                        <Box key={idx} sx={{ display: tabIndex === 1 ? 'block' : 'none' }}>
                            {/* Web Preview */}
                            <Box sx={{ width: '100%', }}>
                                <img
                                    src={url}
                                    alt={`Web Preview ${idx + 1}`}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        objectFit: 'cover',
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}
                </DialogContent>
            </Dialog>
        </FormControl>
    );
};

export default ImageUploadField;
